import { Comment } from '@ninety/platform/models/_shared/comment';
import { Issue } from '@ninety/platform/models/issues/issue';
import { Headline } from '@ninety/platform/models/meetings/headline';
import { NotificationItemType } from '@ninety/platform/models/notifications/notification-item-type';
import { Milestone } from '@ninety/platform/models/rocks/milestone';
import { Rock } from '@ninety/platform/models/rocks/rock';
import { Todo } from '@ninety/platform/models/todos/todo';

import { InboxItem, InboxLinkedItem, InboxParticipant } from './inbox-item.model';

/**
 * Names and avatars come from the users the app already holds, so the mappers ask for an owner
 * rather than building one. Each type passes its own fallback name — the payload sometimes
 * embeds a user the directory does not.
 */
export type OwnerResolver = (userId: string | undefined | null, fallbackName?: string) => InboxParticipant;

function sortedComments(comments: Comment[] | null | undefined): Comment[] | undefined {
  if (!comments?.length) return undefined;
  return [...comments].sort((a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime());
}

/** Descriptions are authored in a rich text editor; the card shows them as a plain line. */
function toPlainText(html: string): string {
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

interface LinkedFields {
  title?: string;
  description?: string | null;
  teamId?: string;
  date?: Date | string | null;
  owner: InboxParticipant;
  comments?: Comment[] | null;
}

/**
 * The row already knows the title and the team, and stays the fallback for both — an item the
 * user can no longer read comes back sparse rather than empty.
 */
function linkedItem(item: InboxItem, fields: LinkedFields): InboxLinkedItem {
  return {
    itemType: item.itemType,
    itemId: item.itemId,
    title: fields.title?.trim() || item.itemTitle,
    description: toPlainText(fields.description ?? ''),
    owner: fields.owner,
    teamId: fields.teamId ?? item.teamId ?? '',
    teamName: item.teamName ?? '',
    date: fields.date ? new Date(fields.date) : item.createdAt,
    // Oldest first, the way every item card in the app reads a thread. The payload's order is
    // insertion order, which a merge or an import can leave out of sequence.
    comments: sortedComments(fields.comments),
  };
}

export function todoToLinkedItem(todo: Todo, item: InboxItem, resolveOwner: OwnerResolver): InboxLinkedItem {
  return linkedItem(item, {
    title: todo.title,
    description: todo.description,
    teamId: todo.teamId,
    date: todo.dueDate,
    owner: resolveOwner(todo.userId, todo.user?.fullName),
    comments: todo.comments,
  });
}

export function rockToLinkedItem(rock: Rock, item: InboxItem, resolveOwner: OwnerResolver): InboxLinkedItem {
  return {
    ...linkedItem(item, {
      title: rock.title,
      description: rock.description,
      teamId: rock.teamId,
      date: rock.dueDate,
      owner: resolveOwner(rock.userId, rock.user?.fullName),
      comments: rock.comments,
    }),
    // Already formatted `2026 Q4` by the API — left off rather than derived when it is missing.
    quarter: rock.dueDateQuarter,
  };
}

export function milestoneToLinkedItem(
  milestone: Milestone,
  item: InboxItem,
  resolveOwner: OwnerResolver
): InboxLinkedItem {
  // A milestone lives under its rock, and opening one routes through that rock — so the parent
  // is kept for its id even when nothing names it, and the card simply shows no breadcrumb.
  const rockId = milestone.rockId ?? item.rockId;

  return {
    ...linkedItem(item, {
      title: milestone.title,
      description: milestone.description,
      teamId: milestone.teamId,
      date: milestone.dueDate,
      owner: resolveOwner(milestone.ownedByUserId ?? milestone.userId),
      comments: milestone.comments,
    }),
    parent: rockId
      ? { itemType: NotificationItemType.rock, itemId: rockId, title: milestone.rock?.title ?? '' }
      : undefined,
  };
}

export function issueToLinkedItem(issue: Issue, item: InboxItem, resolveOwner: OwnerResolver): InboxLinkedItem {
  return {
    ...linkedItem(item, {
      title: issue.title,
      description: issue.description,
      teamId: issue.teamId,
      // Issues are dated by when they were raised; they carry a due date only by exception.
      date: issue.createdDate,
      owner: resolveOwner(issue.userId, issue.user?.fullName),
      comments: issue.comments,
    }),
    rating: issue.rating,
  };
}

export function headlineToLinkedItem(
  headline: Headline,
  item: InboxItem,
  resolveOwner: OwnerResolver
): InboxLinkedItem {
  return linkedItem(item, {
    title: headline.title,
    description: headline.description,
    teamId: headline.teamId,
    date: headline.createdDate,
    owner: resolveOwner(headline.ownedByUserId ?? headline.userId, headline.ownedByUser?.fullName),
    comments: headline.comments,
  });
}
